import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import './ManageAccess.css';

const ManageAccess = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [userGroups, setUserGroups] = useState({});
  const [error, setError] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newUserGroupId, setNewUserGroupId] = useState('');
  const [creditsToAdd, setCreditsToAdd] = useState('');
  const [selectedGroupForCredits, setSelectedGroupForCredits] = useState('');
  const [refreshData, setRefreshData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [vat, setVAT] = useState('');

  const countries = [
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BR', name: 'Brazil' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'CA', name: 'Canada' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'EE', name: 'Estonia' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GI', name: 'Gibraltar' },
    { code: 'GR', name: 'Greece' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IT', name: 'Italy' },
    { code: 'JP', name: 'Japan' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MT', name: 'Malta' },
    { code: 'MX', name: 'Mexico' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NO', name: 'Norway' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'RO', name: 'Romania' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'ES', name: 'Spain' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'TH', name: 'Thailand' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
  ];

  const navigate = useNavigate();

  // Helper function to check if the token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    const currentTime = Math.floor(Date.now() / 1000);
    return exp < currentTime;
  };

  const refreshAuthToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return false;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        return true;
      } else {
        console.error('Failed to refresh token');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  const fetchWithAuth = async (url, options = {}) => {
    let token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      const refreshed = await refreshAuthToken();
      if (!refreshed) {
        navigate('/login');
        return null;
      }
      token = localStorage.getItem('token');
    }
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersResponse = await fetchWithAuth(`${process.env.REACT_APP_FQDN}/api/users`);
        const groupsResponse = await fetchWithAuth(`${process.env.REACT_APP_FQDN}/api/groups`);
        const userGroupsResponse = await fetchWithAuth(`${process.env.REACT_APP_FQDN}/api/user-groups`);
        if (usersResponse && groupsResponse && userGroupsResponse) {
          const usersData = await usersResponse.json();
          const groupsData = await groupsResponse.json();
          const userGroupsData = await userGroupsResponse.json();
          setUsers(usersData.users);
          setGroups(groupsData.groups);
          setUserGroups(userGroupsData.userGroups);
        }
      } catch (error) {
        setError('Error fetching data.');
        console.error('Error:', error);
      }
    };
    fetchData();
  }, [refreshData]);

  const handleAddUser = async () => {
    if (!newUsername || !newUserGroupId) {
      alert('All fields are required');
      return;
    }
    try {
      const newToken = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/add-user`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${newToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            username: newUsername,
            group_id: newUserGroupId,
        }),
      });

      const data = await response.json(); 

      if (response.ok) {
        setUsers([...users, { username: newUsername, group_id: newUserGroupId }]);
        setNewUsername('');
        setNewUserGroupId('');
        setRefreshData((prev) => !prev);
        alert(`User '${newUsername}' added successfully!`);
      } else {
        alert(`Failed to add user: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      alert('Error adding user');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName) {
      alert('Group name is required');
      return;
    }
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_FQDN}/api/add-group`, {
        method: 'POST',
        body: JSON.stringify({newGroupName, addressLine1, addressLine2, postcode, city, country, vat}),
      });
      if (response && response.ok) {
        const data = await response.json();
        setGroups([...groups, { id: data.group_id, name: newGroupName, credits_remaining: '0' }]);
        setNewGroupName('');
        setRefreshData((prev) => !prev);
        alert(`Group '${newGroupName}' created successfully!`);
      } else {
        const errorData = await response.json(); // Parse error response
        alert(errorData.message || 'Failed to create group');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Error creating group');
    }
  };

  const handleAddCredits = async () => {
    if (!creditsToAdd || !selectedGroupForCredits) {
      alert('Please enter the value to add and select a group.');
      return;
    }
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_FQDN}/api/add-credits`, {
        method: 'POST',
        body: JSON.stringify({
          groupId: selectedGroupForCredits,
          credits: creditsToAdd,
        }),
      });
      if (response && response.ok) {
        setRefreshData((prev) => !prev);
        alert(`Successfully added ${creditsToAdd} credits to the group.`);
        setCreditsToAdd('');
        setSelectedGroupForCredits('');
      } else {
        alert('Failed to add credits.');
      }
    } catch (error) {
      console.error('Error adding credits:', error);
      alert('Error adding credits');
    }
  };

  const handleChangeUserGroup = async (userId, newGroupId) => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_FQDN}/api/update-user-group`, {
        method: 'POST',
        body: JSON.stringify({ userId, groupId: newGroupId }),
      });
      if (response && response.ok) {
        const foundGroup = groups.find((g) => parseInt(g.id) === parseInt(newGroupId));
        const groupName = foundGroup ? foundGroup.name : 'Unknown Group';
        setUserGroups((prevState) => ({
          ...prevState,
          [userId]: {
            ...prevState[userId],
            groups: [{ group_id: newGroupId, group_name: groupName }],
          },
        }));
        setRefreshData((prev) => !prev);
      } else {
        alert('Failed to update user group.');
      }
    } catch (error) {
      console.error('Error updating user group:', error);
      alert('Error updating user group');
    }
  };

  const totalPages = Math.ceil(Object.keys(userGroups).length / itemsPerPage);
  const paginatedUsers = Object.keys(userGroups).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  return (
    <div>
      <h2>Manage Access</h2>
      {error ? <p className='manageAccessErrorMessage'>{error}</p> : null}

      {/* Add New User Section */}
      <div className="admin-add-user">
        <h3>Add New User</h3>
        <div className="add-user-column">
          <input
            type="text"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            placeholder="Enter username"
            autoComplete="new-username" 
            className="register-input"
          />
          <select
            value={newUserGroupId}
            onChange={(e) => setNewUserGroupId(e.target.value)}
            autoComplete="off" 
            className="register-input"
          >
            <option value="" disabled>
              Select Group
            </option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
          <button onClick={handleAddUser} className='register-input'>Add User</button>
        </div>
      </div>

      {/* Create New Group Section */}
      <h3>Create New Group</h3>
      <div className="create-group-column">
        <input
          type="text"
          className="register-input"
          value={vat}
          onChange={(e) => setVAT(e.target.value)}
          placeholder="Company VAT (optional)"
        />
        <label htmlFor="group" className="register-label"></label>
        <input
          type="text"
          id="group"
          className="register-input"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          placeholder="Company name"
          required
        />
        <input
          type="text"
          className="register-input"
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          placeholder="1st line of the company address"
          required
        />
        <input
          type="text"
          className="register-input"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          placeholder="2nd line of the company address (optional)"
        />
        <input
          type="text"
          className="register-input"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          placeholder="Company postcode/zipcode"
          required
        />
        
        <input
          type="text"
          className="register-input"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Company city"
          required
        />
        <select
          id="country"
          className="register-input"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          required
        >
          <option value="" disabled>Company country</option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
        <button onClick={handleCreateGroup} className='register-input'>Create Group</button>
      </div>

      {/* Add Credits to Group Section */}
      <div className="admin-add-credits">
        <h3>Add Funds to Group</h3>
        <input
          type="number"
          value={creditsToAdd}
          onChange={(e) => setCreditsToAdd(e.target.value)}
          placeholder="Enter a value in pence (e.g., 1000 for £10.00)"
          className="register-input"
        />
        <select
          value={selectedGroupForCredits}
          onChange={(e) => setSelectedGroupForCredits(e.target.value)}
          className="register-input"
        >
          <option value="" disabled>
            Select Group
          </option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
        <button onClick={handleAddCredits} className='register-input'>Add Credits</button>
      </div>

      {/* All Groups Section */}
      <div className="admin-all-groups">
        <h3>All Groups</h3>
        {groups.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th className='th-manage-access'>Group Name</th>
                <th className='th-manage-access'>Account Balance</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id}>
                  <td>{group.name}</td>
                  <td>£{(group.credits_remaining / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Loading groups...</p>
        )}
      </div>

      {/* All Users Section with Pagination */}
      <div className="admin-user-group-management">
        <h3>All Users</h3>
        {users.length > 0 && groups.length > 0 ? (
          <>
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Groups</th>
                  <th>Change Group</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((userId) => {
                  const userGroupData = userGroups[userId] || { groups: [] };
                  return (
                    <tr key={userId}>
                      <td>{userGroupData.username}</td>
                      <td>
                        {userGroupData.groups.length > 0 ? (
                          <ul>
                            {userGroupData.groups.map(({ group_id, group_name }) => (
                              <li key={group_id}>{group_name}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>No groups assigned</p>
                        )}
                      </td>
                      <td>
                        <select
                          onChange={(e) => handleChangeUserGroup(userId, e.target.value)}
                          defaultValue={userGroupData.groups[0]?.group_id || ''}
                        >
                          <option value="" disabled>
                            Select Group
                          </option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls */}
            <div className="pagination-controls">
              <button onClick={handlePreviousPage} disabled={currentPage === 1} className="button-page-controls">
                Previous
              </button>
              <span className="page-indicator">
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={handleNextPage} disabled={currentPage === totalPages} className="button-page-controls">
                Next
              </button>
            </div>
          </>
        ) : (
          <p>Loading users and groups...</p>
        )}
      </div>
    </div>
  );
};

export default ManageAccess;
